import { Injectable } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { HOST_BREAKPOINTS } from './breakpoint.service';

/**
 * Fase P3 (responsive): helper para calcular un `MatDialogConfig` de ancho
 * responsivo a partir del ancho "deseado" en desktop.
 *
 * Contexto: hoy casi ningún diálogo de la app define `width` explícito (la
 * mayoría depende del `maxWidth: 80vw` que trae por defecto Angular Material),
 * lo cual ya es razonablemente seguro en mobile. Los pocos que sí fijan un
 * ancho en px (ej. `lsd.component.ts` con `width: '900px'`) ya combinan ese
 * valor con un `maxWidth` propio o heredado, por lo que NO hay una regresión
 * puntual que corregir hoy.
 *
 * Este servicio es la base para que los diálogos NUEVOS (o los que se migren
 * a futuro) dejen de fijar anchos en px "a mano" y en su lugar pidan un
 * ancho responsivo consistente con los breakpoints canónicos del host (ver
 * `_breakpoints.scss` / `BreakpointService`). En viewports mobile (< 600px)
 * se recomienda ancho completo (~100vw) en vez de un diálogo centrado angosto,
 * replicando el patrón "pantalla completa" que ya usan otras partes de la app
 * (ej. `.side-nav` en mobile).
 *
 * Uso:
 *   this.dialog.open(MyDialogComponent, this.responsiveDialog.getConfig('900px'));
 */
@Injectable({ providedIn: 'root' })
export class ResponsiveDialogService {

  /**
   * Devuelve un `MatDialogConfig` parcial con `width`/`maxWidth`/`maxHeight`
   * apropiados para el viewport actual. Se evalúa en el momento de abrir el
   * diálogo (no es reactivo) porque `MatDialog.open()` no soporta reconfigurar
   * un diálogo ya abierto si el usuario rota el dispositivo o cambia el
   * tamaño de ventana — igual que el resto de la config de `MatDialogConfig`.
   *
   * @param desktopWidth ancho deseado en viewports >= 600px (ej. '900px', '60vw').
   * @param options.mobileFullscreen si es true (default), en mobile (< 600px)
   *   el diálogo ocupa prácticamente toda la pantalla (~100vw / ~100vh).
   */
  getConfig(desktopWidth: string, options: { mobileFullscreen?: boolean } = {}): Partial<MatDialogConfig> {
    const mobileFullscreen = options.mobileFullscreen ?? true;

    if (this.isMobileViewport()) {
      return mobileFullscreen
        ? { width: '100vw', maxWidth: '100vw', height: '100vh', maxHeight: '100vh', panelClass: 'responsive-dialog-mobile-fullscreen' }
        : { width: '95vw', maxWidth: '95vw', maxHeight: '90vh' };
    }

    return { width: desktopWidth, maxWidth: '95vw', maxHeight: '90vh' };
  }

  /**
   * Evaluación puntual (no observable) del breakpoint `xs`, usando la MISMA
   * media query que `BreakpointService.isHandset$` / `bp-xs` en SCSS.
   */
  private isMobileViewport(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(HOST_BREAKPOINTS.xs).matches;
  }
}
